import React, { useMemo } from 'react'
import dayjs from 'dayjs'
import {
  ErrorBoundary,
  RPCs,
  RPCDocs,
  useRPC,
  ReportTypesPieChart,
  ReportTypesPieChartProps,
  DayOfWeekBarChart,
  ReportTypesStackedBarChart,
  ReportTypesStackedBarChartProps,
} from '@therms/react-modules'
import { Icon, Loading } from '@therms/atalaya'
import { MdAreaChart } from 'react-icons/md'

interface ReportsHistogramsProps {
  endDate?: string
  refs?: string[]
  startDate: string
}

function ReportsHistograms({
  endDate = new Date().toISOString(),
  refs,
  startDate,
}: ReportsHistogramsProps) {
  const calendarInterval: 'day' | 'month' = 'day'

  const { data } = useRPC<
    RPCDocs.Stats.GetReportsHistogram.v1.Args,
    RPCDocs.Stats.GetReportsHistogram.v1.Data
  >(
    RPCs.Stats.GetReportsHistogram.v1,
    {
      calendarInterval: calendarInterval as any,
      fromDate: startDate,
      refs,
      toDate: endDate,
    },
    {
      cacheStrategy: 'cache-only',
      timeout: 60_000,
      transport: 'http',
    },
  )

  const chartData = useMemo(() => {
    if (!data) return undefined

    const reportTypesAggMap = new Map<
      string,
      { color?: string; count: number; name: string }
    >()

    const reportTypesHistogram: ReportTypesStackedBarChartProps['reportsHistogram'] =
      Object.entries(data.histogram).map(
        ([isoDate, { count, reportTypes }]) => {
          reportTypes.forEach((reportType) => {
            const reportTypeData = reportTypesAggMap.get(reportType.name) || {
              color: reportType.color,
              count: 0,
              name: reportType.name,
            }

            reportTypeData.count += reportType.count

            reportTypesAggMap.set(reportType.name, reportTypeData)
          })

          return {
            count,
            formattedDate:
              calendarInterval === 'day'
                ? dayjs(isoDate).format('M/D')
                : dayjs(isoDate).format('MMM'),
            isoDateString: isoDate,
            reportTypes,
          }
        },
      )

    const reportTypeCount: ReportTypesPieChartProps['reportTypeCount'] =
      Array.from(reportTypesAggMap.values()).map(({ color, count, name }) => ({
        color,
        count,
        reportType: name,
      }))

    return {
      reportTypeCount,
      reportTypesHistogram,
    }
  }, [data])

  if (data && !data.totalCount) {
    return (
      <div className="mt-xxl flex flex-col items-center text-lg text-neutral">
        <Icon Icon={MdAreaChart} size="xxl" />
        <div>No Report stats to show</div>
      </div>
    )
  }

  return (
    <div className="relative">
      {!chartData || !data ? (
        <div className="flex justify-center">
          <Loading size="lg" />
        </div>
      ) : (
        <div className="space-y-base">
          <ErrorBoundary>
            <ReportTypesStackedBarChart
              reportsHistogram={chartData.reportTypesHistogram}
            />
          </ErrorBoundary>

          <ErrorBoundary>
            <ReportTypesPieChart reportTypeCount={chartData.reportTypeCount} />
          </ErrorBoundary>

          <ErrorBoundary>
            <DayOfWeekBarChart dowCount={data.dow as any} />
          </ErrorBoundary>
        </div>
      )}
    </div>
  )
}

export { ReportsHistograms }
